import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { User, UserRole } from 'shared/types/user';
import * as authService from 'shared/services/authService';
import { LoginCredentials, RegisterData } from 'shared/services/authService';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<User>;
  register: (data: RegisterData) => Promise<User>;
  logout: () => Promise<void>;
  impersonate: (role: UserRole) => void;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Este é um projeto de apresentação: a navegação não exige login real.
    // Sem persona escolhida, o visitante cai na home de busca (marketplace).
    setUser(authService.getStoredUser());
    setIsLoading(false);
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    const loggedUser = await authService.login(credentials);
    setUser(loggedUser);
    return loggedUser;
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    const registeredUser = await authService.register(data);
    setUser(registeredUser);
    return registeredUser;
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
  }, []);

  const impersonate = useCallback((role: UserRole) => {
    setUser(authService.impersonate(role));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: !!user, isLoading, login, register, logout, impersonate }),
    [user, isLoading, login, register, logout, impersonate]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
